import { useState } from "react";
import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import { Can } from "@/components/Can";
import type { Cargo } from "@/types/api";

interface CargoFormValues {
  name: string;
  weight_brut_kg: number | string;
  weight_net_kg: number | string;
  volume_m3: number | string;
  places_count: number | string;
  unit: string;
  is_dangerous: boolean;
  cargo_value: number | string;
  notes: string;
}

function fmtNum(v?: number): string {
  if (v === undefined || v === null) return "—";
  return v.toLocaleString("ru-RU");
}

interface CargoTabProps {
  orderId?: string;
}

export function CargoTab({ orderId }: CargoTabProps) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Cargo | null>(null);

  const { data: cargoList, isLoading } = useQuery({
    queryKey: qk.orderCargo(orderId ?? ""),
    queryFn: async () => {
      const r = await api.get<Cargo[]>(`/api/v1/orders/${orderId}/cargo`);
      return Array.isArray(r.data) ? r.data : [];
    },
    enabled: !!orderId,
    staleTime: 0,
  });

  const form = useForm<CargoFormValues>({
    initialValues: {
      name: "",
      weight_brut_kg: "",
      weight_net_kg: "",
      volume_m3: "",
      places_count: "",
      unit: "",
      is_dangerous: false,
      cargo_value: "",
      notes: "",
    },
    validate: {
      name: (v) => (v.trim() ? null : "Наименование обязательно"),
    },
  });

  function openCreate() {
    setEditing(null);
    form.reset();
    setModalOpen(true);
  }

  function openEdit(c: Cargo) {
    setEditing(c);
    form.setValues({
      name: c.name,
      weight_brut_kg: c.weight_brut_kg ?? "",
      weight_net_kg: c.weight_net_kg ?? "",
      volume_m3: c.volume_m3 ?? "",
      places_count: c.places_count ?? "",
      unit: c.unit ?? "",
      is_dangerous: c.is_dangerous,
      cargo_value: c.cargo_value ?? "",
      notes: c.notes ?? "",
    });
    setModalOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (values: CargoFormValues) => {
      const body = {
        name: values.name,
        weight_brut_kg: values.weight_brut_kg !== "" ? Number(values.weight_brut_kg) : undefined,
        weight_net_kg: values.weight_net_kg !== "" ? Number(values.weight_net_kg) : undefined,
        volume_m3: values.volume_m3 !== "" ? Number(values.volume_m3) : undefined,
        places_count: values.places_count !== "" ? Number(values.places_count) : undefined,
        unit: values.unit || undefined,
        is_dangerous: values.is_dangerous,
        cargo_value: values.cargo_value !== "" ? Number(values.cargo_value) : undefined,
        notes: values.notes || undefined,
      };
      if (editing) {
        await api.patch(`/api/v1/orders/${orderId}/cargo/${editing.id}`, body);
      } else {
        await api.post(`/api/v1/orders/${orderId}/cargo`, body);
      }
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Сохранено", message: "Груз сохранён" });
      qc.invalidateQueries({ queryKey: qk.orderCargo(orderId!) });
      setModalOpen(false);
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (cid: string) => {
      await api.delete(`/api/v1/orders/${orderId}/cargo/${cid}`);
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Удалено", message: "Груз удалён" });
      qc.invalidateQueries({ queryKey: qk.orderCargo(orderId!) });
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  if (!orderId) {
    return (
      <Paper p="xl" withBorder>
        <Text c="dimmed" ta="center" size="sm">
          Сначала сохраните заявку, затем добавьте груз.
        </Text>
      </Paper>
    );
  }

  const rows = cargoList ?? [];

  return (
    <>
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Title order={5}>Груз</Title>
            <Can perm="orders:write">
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
                Добавить
              </Button>
            </Can>
          </Group>

          {isLoading ? (
            <Text size="sm" c="dimmed">Загрузка…</Text>
          ) : rows.length === 0 ? (
            <Text size="sm" c="dimmed">Груз не добавлен.</Text>
          ) : (
            <Table striped withTableBorder withColumnBorders fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Наименование</Table.Th>
                  <Table.Th>Масса брутто, кг</Table.Th>
                  <Table.Th>Масса нетто, кг</Table.Th>
                  <Table.Th>Объём, м³</Table.Th>
                  <Table.Th>Мест</Table.Th>
                  <Table.Th>Ед.</Table.Th>
                  <Table.Th>Опасный</Table.Th>
                  <Table.Th>Стоимость</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((c, idx) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>{idx + 1}</Table.Td>
                    <Table.Td>{c.name}</Table.Td>
                    <Table.Td>{fmtNum(c.weight_brut_kg)}</Table.Td>
                    <Table.Td>{fmtNum(c.weight_net_kg)}</Table.Td>
                    <Table.Td>{fmtNum(c.volume_m3)}</Table.Td>
                    <Table.Td>{c.places_count ?? "—"}</Table.Td>
                    <Table.Td>{c.unit || "—"}</Table.Td>
                    <Table.Td>{c.is_dangerous ? "Да" : "Нет"}</Table.Td>
                    <Table.Td>{fmtNum(c.cargo_value)}</Table.Td>
                    <Table.Td>
                      <Can perm="orders:write">
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(c)}>
                            <IconEdit size={14} />
                          </ActionIcon>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            loading={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate(c.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </Can>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Редактировать груз" : "Добавить груз"}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Наименование груза"
            required
            {...form.getInputProps("name")}
          />
          <Group grow>
            <NumberInput
              label="Масса брутто, кг"
              placeholder="0"
              min={0}
              decimalScale={3}
              {...form.getInputProps("weight_brut_kg")}
            />
            <NumberInput
              label="Масса нетто, кг"
              placeholder="0"
              min={0}
              decimalScale={3}
              {...form.getInputProps("weight_net_kg")}
            />
          </Group>
          <Group grow>
            <NumberInput
              label="Объём, м³"
              placeholder="0"
              min={0}
              decimalScale={3}
              {...form.getInputProps("volume_m3")}
            />
            <NumberInput
              label="Количество мест"
              placeholder="0"
              min={0}
              {...form.getInputProps("places_count")}
            />
          </Group>
          <Group grow>
            <TextInput label="Единица измерения" placeholder="шт, кг, паллет…" {...form.getInputProps("unit")} />
            <NumberInput
              label="Стоимость груза"
              placeholder="0"
              min={0}
              decimalScale={2}
              {...form.getInputProps("cargo_value")}
            />
          </Group>
          <Checkbox
            label="Опасный груз"
            checked={form.values.is_dangerous}
            onChange={(e) => form.setFieldValue("is_dangerous", e.currentTarget.checked)}
          />
          <Textarea label="Примечания" minRows={2} {...form.getInputProps("notes")} />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setModalOpen(false)}>Отмена</Button>
            <Button
              loading={saveMutation.isPending}
              onClick={() => {
                const result = form.validate();
                if (!result.hasErrors) saveMutation.mutate(form.values);
              }}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
